export const RELAY_METHODS = [
  'colissimo_relay',
  'chronopost_relay',
  'chronopost_relay_shop_to_shop',
]

export const COLISSIMO_DAY_FIELDS = [
  'horairesOuvertureLundi',
  'horairesOuvertureMardi',
  'horairesOuvertureMercredi',
  'horairesOuvertureJeudi',
  'horairesOuvertureVendredi',
  'horairesOuvertureSamedi',
  'horairesOuvertureDimanche',
]

const SLOT_PATTERN = /(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/g

function parseSlots(value) {
  if (typeof value !== 'string') return []
  return [...value.matchAll(SLOT_PATTERN)]
    .map(([, start, end]) => ({
      start: start.padStart(5, '0'),
      end: end.padStart(5, '0'),
    }))
    .filter(({ start, end }) => start !== end)
}

export function mergeSlots(slots) {
  return [...slots]
    .sort((a, b) => a.start.localeCompare(b.start))
    .reduce((merged, slot) => {
      const last = merged[merged.length - 1]
      if (last && slot.start <= last.end) {
        if (slot.end > last.end) last.end = slot.end
      } else {
        merged.push({ ...slot })
      }
      return merged
    }, [])
}

export function relayOpeningHours(relay) {
  const byDay = Array.from({ length: 7 }, () => [])
  if (Array.isArray(relay?.listeHoraireOuverture)) {
    relay.listeHoraireOuverture.forEach(item => {
      const day = Number(item?.jour)
      if (day >= 1 && day <= 7)
        byDay[day - 1].push(...parseSlots(item.horairesAsString))
    })
  } else {
    COLISSIMO_DAY_FIELDS.forEach((field, index) => {
      byDay[index].push(...parseSlots(relay?.[field]))
    })
  }
  return byDay.map((slots, index) => ({
    day: index + 1,
    slots: mergeSlots(slots),
  }))
}

export function hasOpeningHours(hours) {
  return hours.some(({ slots }) => slots.length > 0)
}

export function isoWeekday(date = new Date()) {
  const day = date.getDay()
  return day === 0 ? 7 : day
}

export function formatSlots(slots) {
  return slots.map(({ start, end }) => `${start}\u00a0–\u00a0${end}`).join(', ')
}

export function formatDistance(meters, locale = 'fr') {
  const value = Number(meters)
  if (!Number.isFinite(value) || value < 0) return ''
  const inKilometers = value >= 1000
  const amount = inKilometers ? value / 1000 : Math.round(value)
  try {
    return new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: inKilometers ? 'kilometer' : 'meter',
      unitDisplay: 'short',
      maximumFractionDigits: inKilometers && amount < 10 ? 1 : 0,
    }).format(amount)
  } catch (error) {
    return inKilometers ? `${amount.toFixed(1)} km` : `${amount} m`
  }
}

export function relayAddressLine(relay) {
  return [relay?.adresse1, relay?.adresse2, relay?.adresse3]
    .map(part => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(', ')
}

const LOCKER_PATTERN =
  /\b(consigne|pickup station|packstation|locker|casier)\b/i
const POST_OFFICE_PATTERN = /^(bureau de poste|la poste)\b/i
const DAY_MS = 24 * 60 * 60 * 1000

export function relayKind(relay) {
  const name = relay?.nom || ''
  if (LOCKER_PATTERN.test(name)) return 'locker'
  if (relay?.typeDePoint === 'BPR' || POST_OFFICE_PATTERN.test(name)) {
    return 'postOffice'
  }
  return 'shop'
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function calendarDay(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '')
  return match ? new Date(+match[1], +match[2] - 1, +match[3]) : null
}

function closurePeriods(relay) {
  return [relay?.listeConges, relay?.listePeriodeFermeture]
    .flatMap(value => (Array.isArray(value) ? value : value ? [value] : []))
    .map(period => ({
      start: calendarDay(period?.calendarDeDebut),
      end: calendarDay(period?.calendarDeFin),
    }))
    .filter(({ start, end }) => start && end && end >= start)
}

export function relayClosure(
  relay,
  now = new Date(),
  { horizonDays = 21, minDays = 3 } = {}
) {
  const today = startOfDay(now)
  const horizon = new Date(today.getTime() + horizonDays * DAY_MS)
  const closure = closurePeriods(relay)
    .filter(({ start, end }) => (end - start) / DAY_MS + 1 >= minDays)
    .filter(({ start, end }) => start <= horizon && end >= today)
    .sort((a, b) => a.start - b.start)[0]
  return closure ? { ...closure, current: closure.start <= today } : null
}

export function sortRelayPoints(relays, { preferredId = null, now } = {}) {
  const rank = relay => {
    if (preferredId && relay.identifiant === preferredId) return 0
    return relayClosure(relay, now) ? 2 : 1
  }
  return relays
    .map((relay, index) => ({ relay, index, rank: rank(relay) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ relay }) => relay)
}

export function defaultOpenRelayMethodId(
  relayRanges,
  habitualMethodIds,
  relayChosen
) {
  if (relayChosen) return null
  const habitual = relayRanges.find(range =>
    habitualMethodIds.includes(range.shipping_method?.id)
  )
  return habitual?.shipping_method?.id ?? null
}

export function formatShortDate(date, locale = 'fr', now = new Date()) {
  const options = { day: 'numeric', month: 'short' }
  if (date.getFullYear() !== now.getFullYear()) options.year = 'numeric'
  try {
    return new Intl.DateTimeFormat(locale, options).format(date)
  } catch (error) {
    return date.toLocaleDateString()
  }
}

const PARTICLES = [
  'de',
  'du',
  'des',
  'la',
  'le',
  'les',
  'et',
  'sur',
  'en',
  'au',
  'aux',
]

export function softenCapitals(text) {
  if (!text || text !== text.toUpperCase()) return text

  return text
    .toLowerCase()
    .split(' ')
    .map((word, index) =>
      index && PARTICLES.includes(word)
        ? word
        : word.replace(
            /(^|[-'’])(\p{L})/gu,
            (match, separator, letter) => separator + letter.toUpperCase()
          )
    )
    .join(' ')
}
